import { defineEffect } from '../../../core/effect';
import { pulse, seeded, seg, tween } from '../../../core/effect/motion';
import {
  add, canvasOf, fade, fill, place, playerColor, sprite,
} from '../fx/common';
import turnGear from '../fx/turnGear.png';
import turnPlate from '../fx/turnPlate.png';

/**
 * **ターンの始まり**（Issue #550 v5。指示書 `../direction/new_turn.md`、絵 `../look/new_turn-key.png`）。
 *
 * ```
 * 0      画面がわずかに沈む
 * 100    溜め: 鋼のプレートと歯車の**影**が中央にうっすら浮かぶ（少し大きい所から寄ってくる）
 * 320    本物のプレートが右上から斜めに突き刺さる
 * 400    ばんっ！ 銀の「TURN N」が叩きつけられ、直線の火花と鋼の破片 → **ヒットストップ 100ms**（全部止める）
 * 500    揺れ → 見得（歯車だけがゆっくり回る）
 * 1300   プレートと文字が横に細く潰れて消える
 * 1400   ターンプレイヤーの歯車だけが上の HUD の紋章へ吸い込まれる
 * 1600   歯車が紋章に収まり、HUD の紋章が脈打つ
 * ```
 *
 * **誰のターンか**は歯車で見せる（元の狙い「ターンプレイヤーが入れ替わったこと」）:
 * 大きい歯車の窓に**ターンプレイヤーのデッキの絵**、そのまわりに**プレイヤーの色の硬い輪**。
 * 歯車は**ターンプレイヤーの側の角**（P1 = 左下 / P2 = 右上。HUD の顔の枠と同じ側）に付く。
 * もう一方の角の歯車は飾り（中にもう 1 つ小さい歯車）。
 *
 * 素材は Gemini で生成（`../fx/turnPlate.png` / `../fx/turnGear.png`。緑を抜いたもの）。歯車は HUD の紋章の歯車と同じ形なので、
 * 最後に紋章へ重なって消える。もやもやした光は使わない（火花は硬い線、閃光はヒットストップの間だけ）。
 */
const D = 1600;
const DIM_IN = 120;
const GHOST = 100;          // 溜め（影が浮かぶ）
const STAB = 260;           // プレートが画面の外（カメラの手前）から落ち始める
const HIT = 400;
const STOP = 100;           // ヒットストップ
const SQUASH = 1300;
const SQUASH_END = 1390;
const FLY = 1400;
const LAND = 1570;

const PLATE_W = 1120;
const PLATE_ASPECT = 1200 / 449;
const PLATE_H = PLATE_W / PLATE_ASPECT;
const TILT = -0.12;         // 右上がり（舞台のピクセルは y 下向き）
const DROP_ZOOM = 4.5;      // 落ち始めの大きさ（画面からはみ出す＝カメラの手前）
const DROP_SPIN = 0.45;     // 落ちながら回って TILT に収まる（rad）
const GEAR = 330;           // ターンプレイヤーの歯車の直径
const DECO = 230;           // 飾りの歯車の直径
const WINDOW_R = 0.232;     // 歯車の画像の中の黒い窓の半径（幅に対する比。画像から測った値）
const SPARKS = 26;
const SHARDS = 18;

/** ヒットストップの時間の写し（叩きつけの後の動きは STOP だけ遅れる） */
const hs = (t) => (t < HIT ? t : (t < HIT + STOP ? HIT : t - STOP));

export default defineEffect({
  situation: 'new_turn',
  role: 'effect',
  label: 'ターンの始まり（鋼のプレートを叩きつける）',
  color: '#b01f2a',
  duration: () => D,
  // 攻撃宣言が盤に置いた矢印と小さいパネルはバトルが終わったら消す（Issue #552）
  clears: () => [{ mark: 'attackArrow' }, { mark: 'battlePanel' }],
  scene: [
    'reset',
    {
      ms: HIT,
      // 止まっている間はコントラストを上げて硬く、止めが解けたら揺らす
      grade: {
        contrast: { keys: [{ ms: 0, value: 1.45 }, { ms: STOP, value: 1.45, ease: 'hold' }, { ms: STOP + 1, value: 1 }] },
        chroma: { keys: [{ ms: 0, value: 0.012 }, { ms: STOP + 200, value: 0, ease: 'ease_out' }] },
      },
    },
    { ms: HIT + STOP, shake: { amp: 52, freq: 22, decayMs: 520 } },
  ],
  sounds: () => [
    { at: 0, name: 'turn_change', gain: 0.7 },
    { at: HIT - 15, name: 'attack', gain: 0.9 },
    { at: LAND - 20, name: 'set_card', gain: 0.7 },
  ],
  hud: ({ localMs }) => [{
    anchor: 'turnBadge',
    pulse: pulse(localMs, { at: LAND, inMs: 15, outMs: D - LAND - 15 }),
    flash: 0.5 * pulse(localMs, { at: LAND, inMs: 0, outMs: D - LAND }),
  }],

  layers: [{
    id: 'plate',
    label: '鋼のプレートと歯車（叩きつけ → 歯車が紋章へ）',
    space: 'screen',
    script: (ctx) => {
      const { THREE, kit } = ctx;
      const { w: W, h: H } = canvasOf(ctx);
      const p = ctx.payload.player === 1 ? 1 : 0;
      const color = playerColor(ctx, p);
      const plateTex = kit.urlTexture(turnPlate);
      const gearTex = kit.urlTexture(turnGear);
      const root = new THREE.Group();
      const cx = W / 2;
      const cy = H * 0.48;
      const cos = Math.cos(TILT); const sin = Math.sin(TILT);
      /** プレートの中の位置 → 舞台 */
      const onPlate = (lx, ly, s = 1) => [cx + (lx * cos - ly * sin) * s, cy + (lx * sin + ly * cos) * s];

      // --- 沈める地
      const dim = add(root, place(fill(ctx, '#050507', W, H), W / 2, H / 2), 0);

      // --- 溜めの影（プレートと 2 つの歯車のシルエット）
      const dark = (tex, w, h) => { const m = sprite(ctx, tex, w, h); m.material.color?.set?.('#15171c'); return m; };
      const ghost = new THREE.Group();
      ghost.add(dark(plateTex, PLATE_W, PLATE_H));
      // 歯車の付く角（P1 = 左下、P2 = 右上）
      const side = p === 1 ? 1 : -1;
      const gearLocal = [side * (PLATE_W / 2 - 40), -side * (PLATE_H / 2 - 30)];
      const decoLocal = [-side * (PLATE_W / 2 - 30), side * (PLATE_H / 2 - 24)];
      const ghostGear = dark(gearTex, GEAR, GEAR); ghostGear.position.set(gearLocal[0], gearLocal[1], 0);
      const ghostDeco = dark(gearTex, DECO, DECO); ghostDeco.position.set(decoLocal[0], decoLocal[1], 0);
      ghost.add(ghostGear); ghost.add(ghostDeco);
      add(root, ghost, 2);

      /** 画像を円く抜いた板（中央を正方形に切り出す）。無ければ暗い地 */
      const circleImage = (img, r) => {
        const base = kit.imageTexture(img);
        let tex = null;
        if (base) {
          tex = base.clone();
          tex.userData = {};   // この帯のもの（帯と一緒に捨てる）
          const a = (img.naturalWidth || img.width || 1) / (img.naturalHeight || img.height || 1);
          if (a > 1) { tex.repeat?.set?.(1 / a, 1); tex.offset?.set?.((1 - 1 / a) / 2, 0); } else if (a < 1) { tex.repeat?.set?.(1, a); tex.offset?.set?.(0, (1 - a) / 2); }
          tex.needsUpdate = true;
        }
        const m = new THREE.Mesh(new THREE.CircleGeometry(r, 64), kit.plain(tex, 1));
        if (!tex) m.material.color?.set?.('#1b1d24');
        return m;
      };

      // --- 逆側の歯車（窓にターンプレイヤー本人のアイコン。ユーザー 2026-09-26）
      const deco = new THREE.Group();
      const decoOuter = sprite(ctx, gearTex, DECO, DECO);
      const iconR = DECO * WINDOW_R;
      const iconRing = new THREE.Mesh(new THREE.CircleGeometry(iconR + 3, 64), kit.plain(null, 1));
      iconRing.material.color?.set?.(color);
      const iconBack = new THREE.Mesh(new THREE.CircleGeometry(iconR - 3, 64), kit.plain(null, 1));
      iconBack.material.color?.set?.('#0b0c10');
      const icon = circleImage(ctx.cards?.playerIcon?.(p) ?? null, iconR - 4);
      deco.add(decoOuter); deco.add(iconRing); deco.add(iconBack); deco.add(icon);
      add(root, deco, 6);
      decoOuter.renderOrder = 6; iconRing.renderOrder = 6.1; iconBack.renderOrder = 6.2; icon.renderOrder = 6.3;

      // --- プレートと文字
      const plate = add(root, sprite(ctx, plateTex, PLATE_W, PLATE_H), 4);
      const word = kit.metalTextTexture(`TURN ${ctx.payload.turn ?? ''}`, { size: 250, edge: null, italic: false });
      const fit = Math.min(1, (PLATE_W * 0.78) / Math.max(1, word.width), (PLATE_H * 1.05) / Math.max(1, word.height));
      const title = add(root, sprite(ctx, word.texture, word.width, word.height), 5);

      // --- ターンプレイヤーの歯車（色の輪 → デッキの絵 → 歯車の枠。歯車の枠だけ回す）
      const gear = new THREE.Group();
      const winR = GEAR * WINDOW_R;
      const ring = new THREE.Mesh(new THREE.CircleGeometry(winR + 4, 64), kit.plain(null, 1));
      ring.material.color?.set?.(color);
      // 絵の地（デッキの絵は透過のことがあるので、色の輪が透けないよう暗い円を敷く）
      const back = new THREE.Mesh(new THREE.CircleGeometry(winR - 5, 64), kit.plain(null, 1));
      back.material.color?.set?.('#0b0c10');
      const face = circleImage(ctx.cards?.portrait?.(p) ?? null, winR - 6);
      const gearFrame = sprite(ctx, gearTex, GEAR, GEAR);
      /*
       * 枠（歯車）は窓の縁まで不透明なので、**色の輪とデッキの絵は枠の上**に置く（窓の黒を覆う）。
       * 窓の半径ぴったりに合わせてあるので、歯車の金の縁の内側に色の輪が見える
       */
      gear.add(gearFrame); gear.add(ring); gear.add(back); gear.add(face);
      add(root, gear, 7);
      gearFrame.renderOrder = 7; ring.renderOrder = 8; back.renderOrder = 9; face.renderOrder = 10;

      // --- 火花（黄と白の硬い線）と鋼の破片（三角）
      const rand = seeded(`${ctx.seed}:sparks`);
      const sparkCols = ['#ffffff', '#ffd54a', '#ffb020', '#fff2b0'];
      const sparks = [];
      for (let i = 0; i < SPARKS; i += 1) {
        const m = fill(ctx, sparkCols[i % sparkCols.length], 1, 1);
        const a = rand() * Math.PI * 2;
        sparks.push({ m, a, len: 140 + rand() * 260, w: 3 + rand() * 4, speed: 900 + rand() * 1500, r0: 120 + rand() * 160 });
        add(root, m, 3);   // プレートの後ろから放射（キービジュアル）
      }
      const shards = [];
      for (let i = 0; i < SHARDS; i += 1) {
        const m = new THREE.Mesh(new THREE.CircleGeometry(1, 3), kit.plain(null, 1));
        m.material.color?.set?.(i % 3 === 0 ? '#e6e9ee' : '#9aa1ad');
        const a = rand() * Math.PI * 2;
        shards.push({ m, a, size: 14 + rand() * 26, stretch: 1.6 + rand() * 1.6, speed: 700 + rand() * 1100, spin: (rand() - 0.5) * 30, r0: 170 + rand() * 150 });
        add(root, m, 6);
      }
      const flash = add(root, place(fill(ctx, '#ffffff', W, H, { blend: 'add' }), W / 2, H / 2), 12);
      const line = add(root, fill(ctx, '#ffffff', 1, 1), 11);

      // 行き先（HUD の紋章の歯車）
      const badge = ctx.stage?.hud?.turnBadge?.() ?? null;
      const bx = badge ? badge.x : W / 2;
      const by = badge ? badge.y + badge.h / 2 : 70;
      const bs = badge ? badge.h / GEAR : 0.4;

      return {
        root,
        update: ({ localMs: t }) => {
          const u = hs(t);   // 叩きつけの後は止めの分だけ遅れる時間
          fade(dim, 0.75 * seg(t, 0, DIM_IN) * (1 - seg(t, FLY, LAND)));

          // --- 溜め: 影が少し大きい所から寄ってくる（本物が来たら消える）
          const g = seg(t, GHOST, STAB + 60);
          const gs = 1.14 - 0.08 * tween(t, GHOST, HIT, 'ease_out');
          place(ghost, cx, cy, { s: gs, rot: TILT });
          fade(ghost, 0.55 * g * (1 - seg(t, HIT - 20, HIT)));
          ghostGear.rotation.z = t / 900;
          ghostDeco.rotation.z = -t / 700;

          /*
           * --- 叩きつけ（ユーザー 2026-09-26）: 画面の外＝カメラの手前の大きさから、回りながら加速して盤へ落ちる。
           * 横から滑り込ませない
           */
          const inK = 1 - tween(t, STAB, HIT, 'power3_in');
          const zoom = 1 + (DROP_ZOOM - 1) * inK;
          const ang = TILT + DROP_SPIN * inK;
          const ca = Math.cos(ang); const sa = Math.sin(ang);
          /** プレートの中の位置 → 舞台（落ちている間の大きさ・回りも込み） */
          const at = (lx, ly, k) => [cx + (lx * ca - ly * sa) * k, cy + (lx * sa + ly * ca) * k];
          const punch = t < HIT ? 1 : 1 + 0.05 * (1 - tween(u, HIT, HIT + 160, 'back_out'));
          // 潰れてはける（縦が潰れ、横に少し伸びる）
          const sq = tween(t, SQUASH, SQUASH_END, 'power2_in');
          const sy = Math.max(0.001, 1 - sq);
          const sx = 1 + 0.18 * sq;
          const shown = t >= STAB && t < SQUASH_END;
          place(plate, cx, cy, { sx: zoom * punch * sx, sy: zoom * punch * sy, rot: ang });
          if (t < HIT) fade(plate, shown ? seg(t, STAB, STAB + 40) : 0);
          fade(plate, shown ? 1 : 0);

          // 飾りの歯車（プレートと一緒に動いて一緒に潰れる）
          const [dx, dy] = at(decoLocal[0] * sx, decoLocal[1] * sy, zoom * punch);
          place(deco, dx, dy, { sx: zoom * sx, sy: zoom * sy });
          decoOuter.rotation.z = -u / 650;
          fade(deco, shown ? seg(t, STAB, STAB + 40) : 0);

          // 文字: 叩きつけ（2.6 倍 → 1 倍）→ 止め → 少し跳ね返って落ち着く
          const slam = tween(t, HIT - 30, HIT, 'power2_in');
          const settle = t < HIT + STOP ? 1 : 1 + 0.05 * Math.sin(seg(u, HIT, HIT + 140) * Math.PI);
          const ts = (2.6 - 1.6 * slam) * settle * fit * punch;
          const [tx, ty] = onPlate(0, -6, 1);
          place(title, tx, ty, { sx: ts * sx, sy: ts * sy, rot: TILT });
          fade(title, t >= HIT - 30 && t < SQUASH_END ? seg(t, HIT - 30, HIT - 10) : 0);

          // 潰れきった瞬間の硬い白い線（1〜2 フレーム）
          const lk = t >= SQUASH_END - 20 && t < SQUASH_END + 20;
          place(line, cx, cy, { sx: PLATE_W * 1.25, sy: 5, rot: TILT });
          fade(line, lk ? 0.9 : 0);

          // --- ターンプレイヤーの歯車（プレートと一緒に入り、1400 から紋章へ吸い込まれる）
          const [gx0, gy0] = at(gearLocal[0] * sx, gearLocal[1], zoom * punch);
          const fly = tween(t, FLY, LAND, 'power2_in');
          const gx = gx0 + (bx - gx0) * fly;
          const gy = gy0 + (by - gy0) * fly;
          const gsc = zoom * (1 + (bs - 1) * fly);
          place(gear, gx, gy, { s: gsc });
          gearFrame.rotation.z = u / 900 + fly * Math.PI * 1.5;
          fade(gear, t >= STAB && t < D - 10 ? 1 - seg(t, LAND, D - 10) : 0);

          // --- 火花と破片（止めの間はその場で止まる）
          const since = Math.max(0, (u - HIT) / 1000);
          const life = 0.42;
          sparks.forEach((s) => {
            const k = Math.min(1, since / life);
            const r = s.r0 + s.speed * since * (1 - 0.4 * k);
            const len = s.len * (1 - k);
            const mid = r + len / 2;   // 内側の端が r
            s.m.position.set(tx + Math.cos(s.a) * mid, ty + Math.sin(s.a) * mid, 0);
            s.m.rotation.z = s.a;
            s.m.scale.set(Math.max(0.001, len), s.w, 1);
            fade(s.m, t >= HIT && k < 1 ? 1 : 0);
          });
          shards.forEach((s) => {
            const k = Math.min(1, since / 0.6);
            const r = s.r0 + s.speed * since;
            s.m.position.set(tx + Math.cos(s.a) * r, ty + Math.sin(s.a) * r + 1400 * since * since, 0);
            s.m.rotation.z = s.a + s.spin * since;
            s.m.scale.set(s.size * s.stretch, s.size, 1);
            fade(s.m, t >= HIT && k < 1 ? 1 - k * k : 0);
          });

          // 閃光（止めの頭の 2 フレームだけ強く）
          fade(flash, t >= HIT && t < HIT + 34 ? 0.14 : 0);
        },
      };
    },
  }],
}, 'new_turn_effect.js');
